import { ref, onUnmounted } from "vue";
import { API_BASE } from "@/config";

export interface ContainerSlot {
  cntrNo: string;
  cBlock: string | null;
  bayIdx: number | null; // 1-based
  rowIdx: number | null; // 1-based
  tier: number | null;
  cArea: string | null;
  status: string; // "F" | "E"
  oprId: string | null; // shipping line
  isoSztp: string | null; // "45G0", "22G0" ...
  localSztp: string | null; // "40HC", "20DC" ...
  cjMode: string | null; // "HBAI", "NTAU" ...
  updateTime: string; // ISO timestamp
}

// Outer key: blockId. Inner key: `${bayIdx}:${rowIdx}:${tier}`
export type BlockContainerMap = Map<string, Map<string, ContainerSlot>>;

function slotKey(bayIdx: number, rowIdx: number, tier: number): string {
  return `${bayIdx}:${rowIdx}:${tier}`;
}

function buildBlockMap(slots: ContainerSlot[]): BlockContainerMap {
  const map: BlockContainerMap = new Map();
  for (const slot of slots) {
    if (
      !slot.cBlock ||
      slot.bayIdx == null ||
      slot.rowIdx == null ||
      slot.tier == null
    )
      continue;
    if (!map.has(slot.cBlock)) map.set(slot.cBlock, new Map());
    map
      .get(slot.cBlock)!
      .set(slotKey(slot.bayIdx, slot.rowIdx, slot.tier), slot);
  }
  return map;
}

/** Merge delta slots into existing map. Returns a new Map to trigger Vue reactivity. */
function mergeBlockMap(
  map: BlockContainerMap,
  slots: ContainerSlot[],
): BlockContainerMap {
  const next = new Map(map);
  for (const slot of slots) {
    if (
      !slot.cBlock ||
      slot.bayIdx == null ||
      slot.rowIdx == null ||
      slot.tier == null
    )
      continue;
    if (!next.has(slot.cBlock)) next.set(slot.cBlock, new Map());
    next
      .get(slot.cBlock)!
      .set(slotKey(slot.bayIdx, slot.rowIdx, slot.tier), slot);
  }
  return next;
}

export type StreamStatus = "idle" | "connecting" | "connected" | "error";

export function useContainerStream() {
  const blockMap = ref<BlockContainerMap>(new Map());
  const streamStatus = ref<StreamStatus>("idle");
  const totalContainers = ref(0);

  let es: EventSource | null = null;

  function connect() {
    if (es) return; // already connected
    streamStatus.value = "connecting";

    es = new EventSource(`${API_BASE}/api/tos/containers/stream`);

    es.addEventListener("snapshot", (e: MessageEvent) => {
      const slots: ContainerSlot[] = JSON.parse(e.data);
      blockMap.value = buildBlockMap(slots);
      totalContainers.value = slots.length;
      streamStatus.value = "connected";
    });

    es.addEventListener("update", (e: MessageEvent) => {
      const slots: ContainerSlot[] = JSON.parse(e.data);
      blockMap.value = mergeBlockMap(blockMap.value, slots);
    });

    es.addEventListener("error", (e: MessageEvent) => {
      try {
        const { error } = JSON.parse(e.data);
        console.error("[ContainerStream] Server error:", error);
      } catch {
        /* plain error event */
      }
      streamStatus.value = "error";
    });

    es.onerror = () => {
      // Browser fires onerror on connection failure or unexpected close.
      // EventSource will attempt auto-reconnect; we just track status.
      if (streamStatus.value === "connected") streamStatus.value = "error";
    };
  }

  function disconnect() {
    es?.close();
    es = null;
    streamStatus.value = "idle";
  }

  onUnmounted(disconnect);

  return { blockMap, streamStatus, totalContainers, connect, disconnect };
}
